import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { useI18n } from '../../i18n'
import type { MessageKey } from '../../i18n/messages/en'
import { useOnlineStatus } from '../connectivity/useOnlineStatus'
import {
  suggestedPlaybackQuality,
  youtubePosterUrl,
} from './dataSaver'
import { loadYoutubeApi } from './loadApi'
import styles from './YoutubePlayer.module.css'

export type PlayerStatus =
  | 'idle'
  | 'deferred'
  | 'loading'
  | 'ready'
  | 'playing'
  | 'paused'
  | 'ended'
  | 'error'
  | 'missing'

export type YoutubePlayerHandle = {
  seekTo: (seconds: number, allowSeekAhead?: boolean) => void
  play: () => void
  pause: () => void
  getCurrentTime: () => number
  getDuration: () => number
  getPlayerState: () => number
  /** True once the YouTube iframe has been created. */
  isActivated: () => boolean
  /** Create the iframe without necessarily playing. */
  activate: () => void
}

type Props = {
  videoId: string | null
  title?: string
  /**
   * When true, show a poster until activate/play.
   * Avoids pulling the media stream while the athlete only browses techniques.
   */
  deferLoad?: boolean
  /** Cue position used when the iframe is first created. */
  startSeconds?: number
  onReady?: (duration: number) => void
  onTime?: (seconds: number) => void
  onStateChange?: (status: PlayerStatus) => void
  onError?: (message: string) => void
  className?: string
}

type PendingSeek = { seconds: number; allowSeekAhead: boolean }

const YT_ERROR_KEYS: Partial<Record<number, MessageKey>> = {
  2: 'youtube.error.invalidId',
  5: 'youtube.error.html5',
  100: 'youtube.error.notFound',
  101: 'youtube.error.embedDisabled',
  150: 'youtube.error.embedDisabled',
}

export const YoutubePlayer = forwardRef<YoutubePlayerHandle, Props>(
  function YoutubePlayer(
    {
      videoId,
      title,
      deferLoad = false,
      startSeconds = 0,
      onReady,
      onTime,
      onStateChange,
      onError,
      className,
    },
    ref,
  ) {
    const { t } = useI18n()
    const displayTitle = title ?? t('youtube.referenceVideo')
    const hostId = useId().replace(/:/g, '')
    const containerRef = useRef<HTMLDivElement | null>(null)
    const playerRef = useRef<YT.Player | null>(null)
    const pollRef = useRef<number | null>(null)
    const [status, setStatus] = useState<PlayerStatus>(() =>
      videoId ? (deferLoad ? 'deferred' : 'idle') : 'missing',
    )
    const [error, setError] = useState<string | null>(null)
    const [activated, setActivated] = useState(() => Boolean(videoId) && !deferLoad)
    const online = useOnlineStatus()
    const callbacks = useRef({ onReady, onTime, onStateChange, onError })
    callbacks.current = { onReady, onTime, onStateChange, onError }
    const tRef = useRef(t)
    tRef.current = t

    const pendingSeek = useRef<PendingSeek | null>(null)
    const pendingPlay = useRef(false)
    const startSecondsRef = useRef(startSeconds)
    startSecondsRef.current = startSeconds
    const activateRequest = useRef(0)

    const playerErrorMessage = (code: number): string => {
      const key = YT_ERROR_KEYS[code]
      if (key) return tRef.current(key)
      return tRef.current('youtube.error.playback', { code: String(code) })
    }

    const destroyPlayer = () => {
      if (pollRef.current !== null) {
        window.clearInterval(pollRef.current)
        pollRef.current = null
      }
      if (playerRef.current) {
        try {
          playerRef.current.destroy()
        } catch {
          /* ignore */
        }
        playerRef.current = null
      }
    }

    const reportStatus = (next: PlayerStatus) => {
      setStatus(next)
      callbacks.current.onStateChange?.(next)
    }

    const createPlayer = (id: string, shouldPlay: boolean) => {
      const requestId = ++activateRequest.current
      reportStatus('loading')
      setError(null)

      loadYoutubeApi()
        .then((YT) => {
          if (requestId !== activateRequest.current) return

          destroyPlayer()

          const container = containerRef.current
          if (!container) {
            const message = tRef.current('youtube.error.loadFailed')
            setError(message)
            reportStatus('error')
            callbacks.current.onError?.(message)
            setActivated(false)
            return
          }

          // React owns the container; mount a fresh host each time because
          // YT.Player.destroy() removes the previous host/iframe from the DOM.
          container.replaceChildren()
          const host = document.createElement('div')
          host.id = `yt-${hostId}`
          container.appendChild(host)

          const cueStart =
            pendingSeek.current?.seconds ??
            (Number.isFinite(startSecondsRef.current) ? startSecondsRef.current : 0)
          const quality = suggestedPlaybackQuality()
          const playAfterReady = shouldPlay || pendingPlay.current

          // Always pass videoId at construction. Creating an empty embed (then
          // cue/load in onReady) makes YouTube report error 2 — “invalid video ID”.
          playerRef.current = new YT.Player(host, {
            videoId: id,
            width: '100%',
            height: '100%',
            playerVars: {
              controls: 0,
              disablekb: 1,
              fs: 0,
              iv_load_policy: 3,
              rel: 0,
              modestbranding: 1,
              playsinline: 1,
              enablejsapi: 1,
              autoplay: playAfterReady ? 1 : 0,
              start: Math.max(0, Math.floor(cueStart)),
            },
            events: {
              onReady: (event) => {
                if (requestId !== activateRequest.current) return

                if (quality) {
                  try {
                    event.target.setPlaybackQuality(quality)
                  } catch {
                    /* YouTube may ignore quality hints */
                  }
                }

                const seek = pendingSeek.current
                pendingSeek.current = null
                if (seek && Math.abs(seek.seconds - cueStart) > 0.05) {
                  event.target.seekTo(seek.seconds, seek.allowSeekAhead)
                }

                if (playAfterReady) {
                  pendingPlay.current = false
                  // Autoplay can be blocked; retry after the user gesture path.
                  event.target.playVideo()
                }

                reportStatus(playAfterReady ? 'playing' : 'ready')
                const duration = event.target.getDuration?.() ?? 0
                callbacks.current.onReady?.(duration)
              },
              onStateChange: (event) => {
                if (requestId !== activateRequest.current) return
                let next: PlayerStatus = 'ready'
                switch (event.data) {
                  case YT.PlayerState.PLAYING:
                    next = 'playing'
                    break
                  case YT.PlayerState.PAUSED:
                    next = 'paused'
                    break
                  case YT.PlayerState.ENDED:
                    next = 'ended'
                    break
                  case YT.PlayerState.BUFFERING:
                    next = 'loading'
                    break
                  case YT.PlayerState.CUED:
                    next = 'ready'
                    break
                  default:
                    next = 'ready'
                }
                reportStatus(next)
              },
              onError: (event) => {
                if (requestId !== activateRequest.current) return
                const message = playerErrorMessage(event.data)
                setError(message)
                reportStatus('error')
                callbacks.current.onError?.(message)
              },
            },
          })
        })
        .catch(() => {
          if (requestId !== activateRequest.current) return
          const message = tRef.current('youtube.error.loadFailed')
          setError(message)
          reportStatus('error')
          callbacks.current.onError?.(message)
          setActivated(false)
        })
    }

    const activate = (opts?: { play?: boolean }) => {
      if (!videoId) return
      if (playerRef.current) {
        if (opts?.play) {
          playerRef.current.playVideo()
        }
        return
      }
      if (opts?.play) pendingPlay.current = true
      setActivated(true)
    }

    useImperativeHandle(ref, () => ({
      seekTo(seconds, allowSeekAhead = true) {
        if (!videoId) return
        if (!playerRef.current) {
          // Stash only — do not create the iframe for browse/select seeks.
          pendingSeek.current = { seconds, allowSeekAhead }
          return
        }
        playerRef.current.seekTo(seconds, allowSeekAhead)
      },
      play() {
        if (!videoId) return
        if (!playerRef.current) {
          pendingPlay.current = true
          activate({ play: true })
          return
        }
        playerRef.current.playVideo()
      },
      pause() {
        playerRef.current?.pauseVideo()
      },
      getCurrentTime() {
        return playerRef.current?.getCurrentTime?.() ?? pendingSeek.current?.seconds ?? 0
      },
      getDuration() {
        return playerRef.current?.getDuration?.() ?? 0
      },
      getPlayerState() {
        return playerRef.current?.getPlayerState?.() ?? -1
      },
      isActivated() {
        return playerRef.current !== null
      },
      activate() {
        activate()
      },
    }))

    // Reset when the video identity changes (and announce deferred/missing to parents).
    useEffect(() => {
      activateRequest.current += 1
      destroyPlayer()
      pendingSeek.current = null
      pendingPlay.current = false
      setError(null)

      if (!videoId) {
        setActivated(false)
        reportStatus('missing')
        return
      }

      if (deferLoad) {
        setActivated(false)
        reportStatus('deferred')
        return
      }

      setActivated(true)
    }, [videoId, deferLoad, hostId])

    // Create the iframe once activated for the current video.
    useEffect(() => {
      if (!videoId || !activated) return
      if (playerRef.current) return

      createPlayer(videoId, pendingPlay.current)

      return () => {
        activateRequest.current += 1
        destroyPlayer()
      }
      // createPlayer reads callbacks/t via refs; videoId+activated gate is enough.
      // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional activation gate
    }, [videoId, activated, hostId])

    // Best-effort time polling while the iframe exists.
    useEffect(() => {
      if (!videoId || !playerRef.current) return
      if (status === 'missing' || status === 'error' || status === 'idle' || status === 'deferred') {
        return
      }

      pollRef.current = window.setInterval(() => {
        const time = playerRef.current?.getCurrentTime?.()
        if (typeof time === 'number' && Number.isFinite(time)) {
          callbacks.current.onTime?.(time)
        }
      }, 250)

      return () => {
        if (pollRef.current !== null) {
          window.clearInterval(pollRef.current)
          pollRef.current = null
        }
      }
    }, [videoId, status])

    if (!videoId) {
      return (
        <div className={`${styles.frame} ${styles.missing} ${className ?? ''}`} role="status">
          <p>{t('youtube.noVideo')}</p>
        </div>
      )
    }

    const showFacade = deferLoad && !activated

    return (
      <div className={`${styles.wrap} ${className ?? ''}`}>
        <div className={styles.frame}>
          {showFacade ? (
            <button
              type="button"
              className={styles.facade}
              onClick={() => activate()}
              disabled={!online}
              aria-label={t('youtube.loadVideo')}
            >
              <img
                className={styles.poster}
                src={youtubePosterUrl(videoId)}
                alt=""
                loading="lazy"
                decoding="async"
                onError={(event) => {
                  const img = event.currentTarget
                  const step = Number(img.dataset.posterStep ?? '0')
                  if (step === 0) {
                    img.dataset.posterStep = '1'
                    img.src = youtubePosterUrl(videoId, 'mqdefault')
                  } else if (step === 1) {
                    img.dataset.posterStep = '2'
                    img.src = youtubePosterUrl(videoId, 'default')
                  }
                }}
              />
              <span className={styles.facadeScrim}>
                <span className={styles.facadeLabel}>{t('youtube.loadVideo')}</span>
                <span className={styles.facadeHint}>{t('youtube.loadVideoHint')}</span>
              </span>
            </button>
          ) : (
            <div
              ref={containerRef}
              className={styles.player}
              title={displayTitle}
            />
          )}
          {status === 'loading' && (
            <div className={styles.overlay} role="status">
              {t('youtube.loadingPlayer')}
            </div>
          )}
          {error && (
            <div className={styles.overlay} role="alert">
              <p>{error}</p>
            </div>
          )}
          {!online && (
            <div className={styles.overlay} role="status">
              <p>{t('youtube.offline')}</p>
            </div>
          )}
        </div>
      </div>
    )
  },
)
